import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Bell, CheckCheck, Trash2, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { clearAllNotifications, clearNotification, getNotifications, readAllNotifications, readNotification } from "@/lib/api";

const relativeTime = (value) => {
  const seconds = Math.round((new Date(value).getTime() - Date.now()) / 1000);
  const formatter = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });
  if (Math.abs(seconds) < 60) return formatter.format(seconds, "second");
  const minutes = Math.round(seconds / 60);
  if (Math.abs(minutes) < 60) return formatter.format(minutes, "minute");
  const hours = Math.round(minutes / 60);
  if (Math.abs(hours) < 24) return formatter.format(hours, "hour");
  return formatter.format(Math.round(hours / 24), "day");
};

export default function NotificationCenter({ placement = "below" }) {
  const navigate = useNavigate();
  const root = useRef(null);
  const popup = useRef(null);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState(null);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);

  const load = () => getNotifications().then((data) => {
    setItems(data.items || []);
    setUnread(data.unreadCount || 0);
  }).catch(() => {});

  useEffect(() => {
    load();
    const timer = setInterval(load, 30000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const close = (event) => {
      if (!root.current?.contains(event.target) && !popup.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener("pointerdown", close);
    return () => document.removeEventListener("pointerdown", close);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const updatePosition = () => {
      const anchor = root.current?.getBoundingClientRect();
      if (!anchor) return;
      const margin = 12;
      const gap = 8;
      const width = Math.min(360, window.innerWidth - margin * 2);
      const narrow = window.innerWidth < 640;
      const preferredLeft = narrow ? margin : placement === "above" ? anchor.left : anchor.right - width;
      const left = Math.max(margin, Math.min(preferredLeft, window.innerWidth - width - margin));
      if (placement === "above" && !narrow) {
        const bottom = window.innerHeight - anchor.top + gap;
        setPosition({ left, width, bottom, top: "auto", maxHeight: Math.max(80, anchor.top - gap - margin) });
      } else {
        const top = narrow ? Math.max(64, anchor.bottom + gap) : anchor.bottom + gap;
        setPosition({ left, width, top, bottom: "auto", maxHeight: Math.max(80, window.innerHeight - top - margin) });
      }
    };
    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [open, placement]);

  const openItem = async (item) => {
    if (!item.readAt) {
      setUnread((value) => Math.max(0, value - 1));
      setItems((all) => all.map((entry) => entry.id === item.id ? { ...entry, readAt: new Date().toISOString() } : entry));
      readNotification(item.id).catch(load);
    }
    setOpen(false);
    if (item.actionUrl) navigate(item.actionUrl);
  };

  const markAll = async () => {
    await readAllNotifications();
    setUnread(0);
    setItems((all) => all.map((item) => ({ ...item, readAt: item.readAt || new Date().toISOString() })));
  };

  const clearItem = (item) => {
    if (!item.readAt) setUnread((value) => Math.max(0, value - 1));
    setItems((all) => all.filter((entry) => entry.id !== item.id));
    clearNotification(item.id).catch(load);
  };

  const clearAll = async () => {
    try {
      await clearAllNotifications();
      setItems([]);
      setUnread(0);
    } catch { load(); }
  };

  return <div className="relative" ref={root}>
    <button type="button" onClick={() => setOpen((value) => !value)} className="relative rounded-md p-2 text-[#A1A1AA] transition-colors hover:bg-white/[0.06] hover:text-[#F5F5F0]" aria-label={`Notifications${unread ? `, ${unread} unread` : ""}`} title="Notifications">
      <Bell className="h-4 w-4" />
      {unread > 0 && <span className="absolute -right-0.5 -top-0.5 min-w-4 rounded-full bg-[#F59E0B] px-1 text-center text-[9px] font-bold leading-4 text-black">{unread > 99 ? "99+" : unread}</span>}
    </button>
    {open && position && createPortal(<div ref={popup} style={{ ...position, zIndex: 2147483646 }} className="fixed flex flex-col overflow-hidden border border-white/10 bg-[#111] shadow-2xl">
      <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
        <div><div className="font-serif-editorial text-lg">Notifications</div><div className="text-[10px] text-[#777]">This week's activity. Clears itself every Sunday.</div></div>
        <div className="flex shrink-0 items-center gap-3">
          {unread > 0 && <button type="button" onClick={markAll} className="inline-flex items-center gap-1 text-[10px] text-[#A1A1AA] hover:text-white" data-testid="notifications-read-all"><CheckCheck className="h-3.5 w-3.5" /> Mark all read</button>}
          {items.length > 0 && <button type="button" onClick={clearAll} className="inline-flex items-center gap-1 text-[10px] text-[#A1A1AA] hover:text-white" data-testid="notifications-clear-all"><Trash2 className="h-3.5 w-3.5" /> Clear all</button>}
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto">
        {!items.length && <div className="px-5 py-10 text-center text-xs text-[#777]">No notifications yet.</div>}
        {items.map((item) => <div key={item.id} className={`group flex items-stretch border-b border-white/[0.06] transition-colors hover:bg-white/[0.04] ${item.readAt ? "" : "bg-[#F59E0B]/[0.06]"}`}>
          <button type="button" onClick={() => openItem(item)} className="min-w-0 flex-1 py-3 pl-4 pr-1 text-left">
            <div className="flex gap-3"><span className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${item.readAt ? "bg-white/15" : "bg-[#F59E0B]"}`} /><div className="min-w-0"><div className="text-sm text-[#F5F5F0]">{item.title}</div><p className="mt-1 text-xs leading-relaxed text-[#A1A1AA]">{item.body}</p><div className="mt-1.5 text-[9px] uppercase tracking-wider text-[#666]">{relativeTime(item.createdAt)}</div></div></div>
          </button>
          {/* Always visible on touch screens; on a pointer device it appears with the row. */}
          <button type="button" onClick={() => clearItem(item)} aria-label={`Clear notification: ${item.title}`} title="Clear" data-testid="notification-clear" className="shrink-0 px-3 text-[#777] transition-opacity hover:text-white focus-visible:opacity-100 sm:opacity-0 sm:group-hover:opacity-100">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>)}
      </div>
    </div>, document.body)}
  </div>;
}
